import React, { useMemo } from 'react';
import {
  Accordion,
  Banner,
  Box,
  IconBadgeStatus,
  Stack,
  Tooltip,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { useQuery } from 'react-query';

import messages from './messages';
import useIntl from '../../hooks/useIntl';
import { get } from '../../utils/api';
import { useApplicantForms } from '../HouseholdApplicationsDrawer/hooks';
import HouseholdMemberCard from '../HouseholdApplicationsDrawer/HouseholdMemberCard';
import { shouldBypassMoveInRequirements } from '../HouseholdApplicationsDrawer/utils';

type Props = {
  pendingApplicants: Array<any>,
  canceledApplicants: Array<any>,
  propertyId: string,
  organizationId: string,
  applicationDecisionStatus: Array<any>,
  approveApplicant: Function,
  cancelApplicant: Function,
  deniedReasons: Array<any>,
  handleMultipleChecklistOptionsChange: Function,
  handleEditApplicationForm: Function,
  selectedProperty: any,
  infoMessage?: string,
  applicationId?: string,
};
function PendingAndCanceledApplicationsDrawer({
  pendingApplicants,
  canceledApplicants,
  propertyId,
  organizationId,
  applicationDecisionStatus,
  approveApplicant,
  cancelApplicant,
  deniedReasons,
  handleMultipleChecklistOptionsChange,
  handleEditApplicationForm,
  selectedProperty,
  infoMessage,
  applicationId,
}: Props) {
  const intl = useIntl();
  const bypassMoveInRequirement =
    shouldBypassMoveInRequirements(selectedProperty);
  const applicants = React.useMemo(() => {
    return [...pendingApplicants, ...canceledApplicants];
  }, [pendingApplicants, canceledApplicants]);
  const { data: formData, isLoading } = useApplicantForms({
    applicants,
    propertyId,
    organizationId,
  });

  const { data: applicantsValidationResult } = useQuery(
    ['residentValidateApplicants', applicationId],
    () =>
      get(
        `/screening-service/screenings/applicants/validate/${applicationId}?isResidentScreening=true`,
      ),
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(applicationId) && pendingApplicants.length > 0,
    },
  );

  const missingOrInvalidFields = useMemo(() => {
    const pendingCustomerIds = new Set(
      pendingApplicants
        .map((a) => a.applicantCustomer?.customer?.id)
        .filter(Boolean),
    );
    const applicantCustomerIds =
      applicantsValidationResult?.parsedErrors
        ?.applicantCustomerIdsWithMissingOrInvalidFields ?? [];
    const hasPendingApplicantWithIssues = applicantCustomerIds.some((id) =>
      pendingCustomerIds.has(id),
    );
    if (!hasPendingApplicantWithIssues) return [];
    return (
      applicantsValidationResult?.parsedErrors?.missingOrInvalidFields ?? []
    ).map((field) => (field.includes('Dob') ? 'Date of Birth' : field));
  }, [applicantsValidationResult, pendingApplicants]);

  const applicantsWithIssues = useMemo(() => {
    const pendingCustomerIds = new Set(
      pendingApplicants
        .map((a) => a.applicantCustomer?.customer?.id)
        .filter(Boolean),
    );
    const applicantCustomerIds =
      applicantsValidationResult?.parsedErrors
        ?.applicantCustomerIdsWithMissingOrInvalidFields ?? [];
    const screeningApplicants =
      applicantsValidationResult?.screeningApplicants ?? [];
    return screeningApplicants
      .filter(
        (a) =>
          a.customerId &&
          pendingCustomerIds.has(a.customerId) &&
          applicantCustomerIds.includes(a.customerId),
      )
      .map((a) => ({
        customerId: a.customerId,
        name: `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim(),
      }));
  }, [applicantsValidationResult, pendingApplicants]);

  const screeningDetailsTooltipContent = useMemo(
    () => (
      <Box p={1}>
        <Typography variant="labelMedium">
          {intl.formatMessage(messages.invalidOrMissingInformation)}
        </Typography>
        {missingOrInvalidFields.length === 0 ? (
          <Box mt={0.5}>
            <Typography variant="bodySmall">
              {intl.formatMessage(messages.none)}
            </Typography>
          </Box>
        ) : (
          <Stack component="ul" spacing={0.5} sx={{ pl: 2, mt: 0.5, mb: 0 }}>
            {missingOrInvalidFields.map((field) => (
              <Typography component="li" variant="bodySmall" key={field}>
                {field}
              </Typography>
            ))}
          </Stack>
        )}
        {applicantsWithIssues.length > 0 && (
          <>
            <Typography variant="labelMedium" display="block" mt={1}>
              {intl.formatMessage(messages.applicantNeedingReview)}
            </Typography>
            <Stack component="ul" spacing={0.5} sx={{ pl: 2, mt: 0.5, mb: 0 }}>
              {applicantsWithIssues.map(({ customerId, name }) => (
                <Typography component="li" variant="bodySmall" key={customerId}>
                  {name}
                </Typography>
              ))}
            </Stack>
          </>
        )}
      </Box>
    ),
    [applicantsWithIssues, intl, missingOrInvalidFields],
  );

  const noPendingApplicants = pendingApplicants.length === 0;
  const noCanceledApplicants = canceledApplicants.length === 0;
  const noPendingOrCanceledApplicants =
    noPendingApplicants && noCanceledApplicants;
  const allItemsComplete = noPendingApplicants;
  return (
    <Accordion
      summary={
        <Stack direction="row" spacing={2} alignItems="center" width="100%">
          <IconBadgeStatus
            variant={
              allItemsComplete || bypassMoveInRequirement
                ? 'complete'
                : 'incomplete'
            }
          />
          <Typography variant="bodyLarge">
            {intl.formatMessage(messages.title)}
          </Typography>
          {applicationId && (
            <Tooltip
              title={screeningDetailsTooltipContent}
              placement="bottom"
              variant="light"
            >
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                onClick={(e) => e.stopPropagation()}
                sx={{ cursor: 'default' }}
              >
                <AlertInfoIcon fontSize="small" color="primary" />
                <Typography variant="bodySmall" color="primary.main">
                  {intl.formatMessage(messages.screeningDetails)}
                </Typography>
              </Stack>
            </Tooltip>
          )}
        </Stack>
      }
      sx={{ marginTop: 0 }}
    >
      {infoMessage && (
        <Box mb={2}>
          <Banner status={'info'} message={infoMessage} />
        </Box>
      )}
      <Stack
        direction="row"
        gap={2}
        sx={{
          overflow: 'auto',
          scrollbarWidth: 'thin',
          pb: noPendingOrCanceledApplicants ? 0 : 4,
          px: 1,
        }}
      >
        {applicants.map((applicant: any) => {
          const applicantChecklist = applicant.checklist;
          const applicantFormData = formData?.[applicant.id];
          const checklistApproved = applicantChecklist?.approved;
          const applicationComplete = applicantFormData?.isComplete;
          const checklistItems = applicantChecklist?.checklistItems || [];
          const isAdult = Boolean(applicant.applicantCustomer);
          const portalUserId =
            applicant.applicantCustomer?.customer?.pu?.id ?? null;
          const isCanceled = canceledApplicants.some(
            (canceledApplicant) => canceledApplicant.id === applicant.id,
          );
          const isPending = pendingApplicants.some(
            (pendingApplicant) => pendingApplicant.id === applicant.id,
          );
          return (
            <HouseholdMemberCard
              key={applicant.id}
              applicant={applicant}
              checklistItems={checklistItems}
              checklistApproved={checklistApproved}
              isLoading={isLoading}
              isAdult={isAdult}
              name={applicant.name}
              leaseSignerType={applicant.applicantType?.name}
              applicationComplete={applicationComplete}
              portalUserId={portalUserId}
              handleEditApplicationForm={() =>
                handleEditApplicationForm(applicant)
              }
              applicationDecisionStatuses={applicationDecisionStatus}
              handleMultipleChecklistOptionsChange={
                handleMultipleChecklistOptionsChange
              }
              // downloadAffordaPortalApplicationPDF={
              //   downloadAffordaPortalApplicationPDF
              // }
              // affordaPortaV1={affordaPortaV1}
              propertyId={propertyId}
              organizationId={organizationId}
              isCanceled={isCanceled}
              isPending={isPending}
              deniedReasons={deniedReasons}
              cancelApplicant={cancelApplicant}
              approveApplicant={approveApplicant}
              selectedProperty={selectedProperty}
            />
          );
        })}
      </Stack>
      <Box
        sx={{
          ml: noPendingOrCanceledApplicants ? 7 : 0,
        }}
      >
        <Typography variant="bodyMedium" color={'text.disabled'}>
          {noPendingOrCanceledApplicants
            ? // eslint-disable-next-line max-len
              'No pending or canceled applications are available to display. Adding a new household member will automatically show their application here.'
            : // eslint-disable-next-line max-len
              'Note: When you approve a pending application, the household member’s card will automatically move to the existing Household Applications drawer.'}
        </Typography>
      </Box>
    </Accordion>
  );
}

export default PendingAndCanceledApplicationsDrawer;
